'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import type { SegmentPageInfo } from '@fff-web/segment';
import { env } from '../../_config/env.client';
import { isBrowser } from '../../_utils/browser';

export type BrazeInitializerProps = {
  /**
   * Page configuration used for analytics and experiment tracking.
   * The `page` field is used to restrict where Braze is initialized.
   */
  pageConfig: SegmentPageInfo;
};

/**
 * Initializes the Braze Web SDK for the `/get-the-box` funnel.
 *
 * This component:
 * - Conditionally initializes Braze only on `/get-the-box` pages (including nested routes like quiz and membership plans).
 * - Uses environment variables for API key and base URL.
 * - Prevents duplicate initialization across renders and React Strict Mode.
 * - Opens a Braze session and logs a basic page-view style custom event for verification.
 *
 * The implementation follows the official Braze Web SDK integration guide
 * (`braze.initialize`, `braze.automaticallyShowInAppMessages`, `braze.openSession`) as
 * documented in the Braze developer guide for Web SDKs
 * (`https://www.braze.com/docs/developer_guide/sdk_integration?sdktab=web`).
 *
 */
export const BrazeInitializer = ({ pageConfig }: BrazeInitializerProps) => {
  const pathname = usePathname();
  const hasInitializedBrazeSdkRef = useRef<boolean>(false);

  useEffect(() => {
    if (
      !isBrowser() ||
      hasInitializedBrazeSdkRef.current ||
      !env.NEXT_PUBLIC_BRAZE_API_KEY ||
      !env.NEXT_PUBLIC_BRAZE_BASE_URL
    ) {
      return;
    }

    hasInitializedBrazeSdkRef.current = true;

    void (async () => {
      // Lazy-load the SDK to ensure it never executes in non-browser contexts.
      const braze = await import('@braze/web-sdk');

      braze.initialize(env.NEXT_PUBLIC_BRAZE_API_KEY, {
        baseUrl: env.NEXT_PUBLIC_BRAZE_BASE_URL,
        enableLogging: env.NEXT_PUBLIC_APP_ENV !== 'prod',
        allowUserSuppliedJavascript: false,
      });

      // Automatically display in-app messages configured in Braze
      braze.automaticallyShowInAppMessages();

      // For now we start an anonymous session.
      // Future tickets can wire `changeUser` after authentication or lead identification.
      braze.openSession();

      // Basic custom event to validate integration and provide a starting point
      // for CRM to build funnels and experiments.
      braze.logCustomEvent('viewed_get_the_box_funnel_page', {
        path: pathname,
        url: window.location.href,
        page: pageConfig.page,
      });
    })();
  }, [pageConfig.page, pathname]);

  return null;
};
